import { InsightsDailyRow, StoreTapDay } from '../../types/marketing';
import { chartGeometry, formatCount, formatDay, formatRate } from './marketingMath';

const APP = 'App Store';
const PLAY = 'Google Play';

function isRtl(): boolean {
  return typeof document !== 'undefined' && (document.documentElement.dir === 'rtl' || document.dir === 'rtl');
}

/** "Taps per day, Oct 14 to Oct 27: 412 taps (160 App Store, 252 Google Play), tap rate 17.8%." */
export function chartSummary(daily: readonly StoreTapDay[]): string {
  if (daily.length === 0) return 'No days in this range.';
  const sum = (k: 'taps' | 'tapsAppStore' | 'tapsGooglePlay' | 'pageLoads') => daily.reduce((a, d) => a + d[k], 0);
  const loads = sum('pageLoads');
  const rate = loads > 0 ? formatRate(sum('taps') / loads) : 'not available';
  return `Taps per day, ${formatDay(daily[0].date)} to ${formatDay(daily[daily.length - 1].date)}: ${formatCount(sum('taps'))} taps `
    + `(${formatCount(sum('tapsAppStore'))} ${APP}, ${formatCount(sum('tapsGooglePlay'))} ${PLAY}), tap rate ${rate}.`;
}

/** Spec 084 — optional extra series drawn as lines on the taps axis (sign-ups dashed with squares, paid with diamonds). */
export type ExtraSeries = Pick<InsightsDailyRow, 'date' | 'signUps' | 'paid'>[];

const SIGNUPS = 'Sign-ups';
const PAID = 'Paid orders';

/**
 * Spec 078 — daily taps as stacked bars (App Store, Google Play) with the tap-rate line on a second axis.
 * Hand-made SVG, like the dashboard sparkline. Every day is also in a visually hidden table and in a <title>
 * on its column, so nothing depends on colour or hover. In RTL the days run right to left.
 */
export default function DailyTapsChart({ daily, extra }: { daily: StoreTapDay[]; extra?: ExtraSeries }) {
  const rtl = isRtl();
  const byDate = new Map((extra ?? []).map((e) => [e.date, e]));
  const hasExtra = !!extra && extra.length > 0;
  const g = chartGeometry(daily, rtl, 720, 260, hasExtra ? Math.max(0, ...extra.map((e) => Math.max(e.signUps, e.paid))) : 0);
  const { plot } = g;
  const h = plot.bottom - plot.top;
  const every = Math.max(1, Math.ceil(daily.length / 8));
  const summary = chartSummary(daily);
  const startX = rtl ? plot.right + 8 : plot.left - 8;
  const endX = rtl ? plot.left - 8 : plot.right + 8;
  const ticks = [0, 0.5, 1];
  const yOf = (v: number) => plot.bottom - (v / g.maxTaps) * h;
  const line = (k: 'signUps' | 'paid') => daily.map((d, i) => ({ x: g.slotCenter(i), v: byDate.get(d.date)?.[k] }))
    .filter((p): p is { x: number; v: number } => p.v != null).map((p) => ({ x: p.x, y: yOf(p.v) }));
  const extraText = (date: string) => {
    const e = byDate.get(date);
    return e ? `, ${e.signUps} ${SIGNUPS.toLowerCase()}, ${e.paid} paid` : '';
  };
  return (
    <figure className="mkt-chart" data-testid="daily-taps-chart">
      <figcaption className="mkt-chart__head">
        <span className="mkt-chart__title">Taps per day</span>
        <span className="mkt-legend" aria-hidden="true">
          <span className="mkt-legend__item"><i className="mkt-swatch mkt-swatch--app" />{APP}</span>
          <span className="mkt-legend__item"><i className="mkt-swatch mkt-swatch--play" />{PLAY}</span>
          <span className="mkt-legend__item"><i className="mkt-swatch mkt-swatch--rate" />Tap rate</span>
          {hasExtra && <span className="mkt-legend__item"><i className="mkt-swatch mkt-swatch--signups" />{SIGNUPS}</span>}
          {hasExtra && <span className="mkt-legend__item"><i className="mkt-swatch mkt-swatch--paid" />{PAID}</span>}
        </span>
      </figcaption>
      <svg viewBox={`0 0 ${g.width} ${g.height}`} role="img" aria-labelledby="mkt-chart-title mkt-chart-desc" className="mkt-chart__svg">
        <title id="mkt-chart-title">Taps per day by store, with tap rate</title>
        <desc id="mkt-chart-desc">{summary}</desc>
        {ticks.map((t) => {
          const y = plot.bottom - t * h;
          return (
            <g key={t} aria-hidden="true">
              <line className="mkt-grid" x1={plot.left} x2={plot.right} y1={y} y2={y} />
              <text className="mkt-axis" x={startX} y={y + 4} textAnchor={rtl ? 'start' : 'end'}>{formatCount(Math.round(g.maxTaps * t))}</text>
              <text className="mkt-axis mkt-axis--rate" x={endX} y={y + 4} textAnchor={rtl ? 'end' : 'start'}>{`${Math.round(g.maxRate * t * 100)}%`}</text>
            </g>
          );
        })}
        {daily.every((d) => d.taps === 0) && (
          <text className="mkt-axis mkt-axis--note" x={(plot.left + plot.right) / 2} y={(plot.top + plot.bottom) / 2} textAnchor="middle">No taps on these days</text>
        )}
        {g.bars.map((b, i) => {
          const d = daily[i];
          return (
            <g key={b.date} data-day={b.date}>
              <rect className="mkt-bar mkt-bar--app" x={b.x} y={b.appY} width={b.width} height={b.appH} />
              <rect className="mkt-bar mkt-bar--play" x={b.x} y={b.playY} width={b.width} height={b.playH} />
              <rect className="mkt-hit" x={g.slotCenter(i) - (plot.right - plot.left) / daily.length / 2} y={plot.top}
                width={(plot.right - plot.left) / daily.length} height={h}>
                <title>{`${formatDay(d.date)}: ${d.taps} taps (${APP} ${d.tapsAppStore}, ${PLAY} ${d.tapsGooglePlay}), ${d.pageLoads} page loads, tap rate ${formatRate(d.tapRate)}${extraText(d.date)}`}</title>
              </rect>
              {i % every === 0 && <text className="mkt-axis" x={g.slotCenter(i)} y={plot.bottom + 18} textAnchor="middle" aria-hidden="true">{formatDay(d.date)}</text>}
            </g>
          );
        })}
        {g.ratePaths.map((path, i) => (
          <g key={i} aria-hidden="true">
            {path.length > 1 && <polyline className="mkt-rate" points={path.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')} />}
            {path.map((p) => <circle key={p.x} className="mkt-rate-dot" cx={p.x} cy={p.y} r="3" />)}
          </g>
        ))}
        {hasExtra && (['signUps', 'paid'] as const).map((k) => {
          const pts = line(k);
          return (
            <g key={k} aria-hidden="true" data-series={k}>
              {pts.length > 1 && <polyline className={`mkt-series mkt-series--${k}`} points={pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')} />}
              {pts.map((p) => (k === 'signUps'
                ? <rect key={p.x} className="mkt-series-dot mkt-series-dot--signUps" x={p.x - 3} y={p.y - 3} width="6" height="6" />
                : <path key={p.x} className="mkt-series-dot mkt-series-dot--paid" d={`M${p.x} ${p.y - 4}l4 4-4 4-4-4z`} />))}
            </g>
          );
        })}
      </svg>
      <table className="sr-only">
        <caption>{summary}</caption>
        <thead><tr><th>Day</th><th>{APP}</th><th>{PLAY}</th><th>Page loads</th><th>Tap rate</th>{hasExtra && <><th>{SIGNUPS}</th><th>{PAID}</th></>}</tr></thead>
        <tbody>
          {daily.map((d) => (
            <tr key={d.date}><td>{formatDay(d.date)}</td><td>{d.tapsAppStore}</td><td>{d.tapsGooglePlay}</td><td>{d.pageLoads}</td><td>{formatRate(d.tapRate)}</td>
              {hasExtra && <><td>{byDate.get(d.date)?.signUps ?? '—'}</td><td>{byDate.get(d.date)?.paid ?? '—'}</td></>}</tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
